import { type FC, type ReactNode, isValidElement, useMemo } from "react";

import {
  Text,
  Code,
  Paragraph,
  Heading,
  SectionHeading,
  Box,
  Row,
} from "@hightouch/htui";
import { Link } from "src/router";
import ReactMarkdown from "react-markdown";
import type { Components, Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import { MarkdownTable } from "./markdown-table";
import { MarkdownImage } from "./markdown-image";
import { CodeBlock } from "./code-block";
import { CodeDisclosure } from "./code-disclosure";
import { format as formatSql } from "sql-formatter";
import { AdArtifactLink } from "./ad-artifact-link";
import { useRegionConfig } from "src/utils/use-region-config";
import {
  BLOCK_SPACING,
  LIST_ITEM_SPACING,
  NESTED_LIST_ITEM_SPACING,
  PROSE_LINE_HEIGHT,
  SECTION_SPACING,
} from "./constants";

export type MarkdownProps = Omit<Options, "components"> & {
  /** Must be referentially stable (module scope or memoized): each renderer is a component type to react-markdown, so a new identity remounts its subtree and resets state such as a code block's toggle. */
  components?: Components;
  disableParagraphs?: boolean;
  useParagraphMargins?: boolean;
  interactionId?: string;
  /**
   * "reading" is the transcript's long-form treatment, "compact" the dense
   * sidebar one; every other surface stays on "default".
   */
  variant?: "compact" | "default" | "reading";
  /**
   * Puts each fenced code block behind a "Show SQL" style toggle that starts
   * open or closed. Omit to render code without a toggle.
   */
  collapsibleCode?: "expanded" | "collapsed";
};

export const Markdown: FC<Readonly<MarkdownProps>> = ({
  disableParagraphs = false,
  useParagraphMargins = false,
  children,
  components: additionalComponents,
  interactionId,
  variant,
  collapsibleCode,
  ...props
}) => {
  const { apiEndpoint } = useRegionConfig();

  // Each renderer is a component type to react-markdown, so a new identity remounts its subtree and resets state such as a code block's toggle.
  const components = useMemo((): Components => {
    // Helper function to build an output src for image resolution
    const resolveImageSrc = (src: string | undefined): string | undefined => {
      if (
        !src ||
        !isRelativeLink(src) ||
        !interactionId ||
        src.startsWith("data:")
      )
        return src;
      const params = new URLSearchParams();
      params.set("filename", src.replace(/^\.\//, ""));
      params.set("interaction_id", interactionId);
      return `${apiEndpoint}/agents/ad-artifacts?${params.toString()}`;
    };

    const isCompact = variant === "compact";
    const isReading = variant === "reading";
    const headingTopMargin = isReading ? SECTION_SPACING : 6;

    return {
      h1: ({ children }) =>
        isCompact ? (
          <Box mt={6} mb={2}>
            <Text size="md" fontWeight="semibold">
              {children}
            </Text>
          </Box>
        ) : (
          <Heading size="xl" mt={headingTopMargin} mb={2}>
            {children}
          </Heading>
        ),
      h2: ({ children }) =>
        isCompact ? (
          <Box mt={6} mb={0}>
            <Text size="md" fontWeight="semibold">
              {children}
            </Text>
          </Box>
        ) : (
          <Heading size="lg" mt={headingTopMargin} mb={2}>
            {children}
          </Heading>
        ),
      h3: ({ children }) => (
        <Heading size="md" mt={isCompact ? 2 : 6} mb={2}>
          {children}
        </Heading>
      ),
      h4: ({ children }) => (
        <SectionHeading mt={6} mb={2}>
          {children}
        </SectionHeading>
      ),
      h5: ({ children }) => (
        <Row mt={4} mb={2}>
          <Text size="lg" fontWeight="semibold">
            {children}
          </Text>
        </Row>
      ),
      p: ({ children }) => (
        <Paragraph
          size="md"
          mb={useParagraphMargins ? BLOCK_SPACING : 0}
          {...{
            lineHeight: PROSE_LINE_HEIGHT,
            // A line introducing a list belongs to it, so it closes to the
            // list's own rhythm rather than sitting a full block away.
            sx: {
              "&:has(+ ul), &:has(+ ol)": { mb: LIST_ITEM_SPACING },
            },
          }}
          {...(isCompact && { color: "text.tertiary" })}
        >
          {children}
        </Paragraph>
      ),
      strong: ({ children }) => (
        <Text
          size="md"
          fontWeight="semibold"
          {...(isCompact && { color: "text.primary" })}
        >
          {children}
        </Text>
      ),
      a: ({ href, children }) => {
        if (isRelativeLink(href)) {
          return (
            <AdArtifactLink href={href ?? ""} interactionId={interactionId}>
              {children}
            </AdArtifactLink>
          );
        }
        // Otherwise, use regular Link
        return (
          <Link
            href={href ?? "#"}
            color="var(--chakra-colors-link-default)"
            fontSize="inherit"
            isExternal={!href?.startsWith("/")}
          >
            {children}
          </Link>
        );
      },
      // TODO: update this to use the ImageThumbnail component
      img: ({ src, alt }) => (
        <MarkdownImage img={{ src: resolveImageSrc(src), alt }} />
      ),
      // A nested list opens at its own item gap, so the first sub-item
      // sits equidistant from its parent line and its sibling.
      ul: ({ children }) => (
        <Box
          as="ul"
          pl={6}
          mb={BLOCK_SPACING}
          style={{ listStyleType: "disc" }}
          sx={{ "li &": { mt: NESTED_LIST_ITEM_SPACING } }}
        >
          {children}
        </Box>
      ),
      ol: ({ children, ...props }) => {
        return (
          <Box
            as="ol"
            pl={6}
            mb={BLOCK_SPACING}
            style={{ listStyleType: "decimal" }}
            start={props.start}
            textStyle="numeric"
            sx={{ "li &": { mt: NESTED_LIST_ITEM_SPACING } }}
          >
            {children}
          </Box>
        );
      },
      li: ({ children }) => (
        // Muted markers let the text carry the contrast; no Chakra prop
        // reaches ::marker.
        <Box
          as="li"
          mb={LIST_ITEM_SPACING}
          pl={1}
          sx={{
            "&::marker": { color: "text.secondary" },
            "li &": { mb: NESTED_LIST_ITEM_SPACING },
            // Loose lists wrap item text in <p> — strip trailing block
            // margins so item gaps come from the li alone, and bind an
            // intro line to the group it introduces.
            "& p:last-child, & ul:last-child, & ol:last-child": { mb: 0 },
            "& p:has(+ ul), & p:has(+ ol)": {
              mb: NESTED_LIST_ITEM_SPACING,
            },
          }}
        >
          <Text
            as="span"
            size="md"
            {...{ lineHeight: PROSE_LINE_HEIGHT }}
            {...(isCompact && { color: "text.secondary" })}
          >
            {children}
          </Text>
        </Box>
      ),
      blockquote: ({ children }) => (
        // Chakra's reset leaves blockquote completely unstyled.
        <Box
          as="blockquote"
          borderLeft="2px solid"
          borderColor="base.border"
          pl={4}
          mb={BLOCK_SPACING}
          sx={{ "& > *:last-child": { mb: 0 } }}
        >
          {children}
        </Box>
      ),
      // Code blocks (fenced with ```)
      pre: ({ children }) => {
        const isCollapsible = collapsibleCode !== undefined;
        const block = (
          <Box
            as="pre"
            // A collapsible block's wrapper owns the bottom margin.
            mb={isCollapsible ? 0 : BLOCK_SPACING}
            p={3}
            borderRadius="md"
            overflowX="auto"
            bg="base.background"
          >
            {children}
          </Box>
        );
        return isCollapsible ? (
          <CodeDisclosure
            defaultIsExpanded={collapsibleCode === "expanded"}
            languageLabel={codeLanguageLabel(children)}
          >
            {block}
          </CodeDisclosure>
        ) : (
          block
        );
      },
      // Inline code using Chakra's Code component
      code: ({ children, ...props }: any) => {
        // In react-markdown v9, inline code won't have a className (language identifier)
        const isInline = !props.className;

        if (isInline) {
          return <Code {...props}>{children}</Code>;
        }

        const stringValue =
          typeof children === "string"
            ? children
            : Array.isArray(children) &&
                children.length === 1 &&
                typeof children[0] === "string"
              ? children[0]
              : null;

        // This should always (almost always?) be the case,
        // But since the types don't enforce this, we also have a generic fallback below using <code>
        if (stringValue) {
          const config = props.className
            ? editorConfigFromClassName[props.className]
            : undefined;

          return (
            <CodeBlock
              value={config ? config.format(stringValue) : stringValue}
              language={config?.language}
              bg="base.background"
            />
          );
        }

        // Block code (fallback, usually wrapped in <pre>)
        return (
          <Box
            as={Code}
            display="block"
            whiteSpace="pre"
            overflowX="auto"
            bg="base.background"
            {...props}
          >
            {children}
          </Box>
        );
      },
      hr: () => <Box as="hr" mt={8} mb={4} />,
      table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
      ...(additionalComponents ?? {}),
    };
  }, [
    additionalComponents,
    apiEndpoint,
    collapsibleCode,
    interactionId,
    useParagraphMargins,
    variant,
  ]);

  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      components={components}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
};

const REMARK_PLUGINS = [remarkGfm];

// Helper to check if a link is relative
// Relative links are those that don't start with http://, https://, /, #, or mailto:
// This includes paths like ./file.md, ../file.md, and file.md
const isRelativeLink = (href: string | null | undefined): boolean => {
  if (!href) return false;
  // Filter out absolute URLs, anchors, mailto links, and absolute paths
  return !/^(https?:\/\/|\/|#|mailto:)/.test(href);
};

// Fence languages are usually lowercase in markdown source, but these read as acronyms.
const languageLabelOverrides: Record<string, string> = {
  sql: "SQL",
  json: "JSON",
};

/** Display name for the fence language on a `pre`'s `code` child, if it has one. */
const codeLanguageLabel = (preChildren: ReactNode): string | undefined => {
  if (!isValidElement<{ className?: string }>(preChildren)) return undefined;
  const language = preChildren.props.className?.match(
    /(?:^|\s)language-(\S+)/,
  )?.[1];
  if (!language) return undefined;
  return languageLabelOverrides[language] ?? language;
};

const editorConfigFromClassName: Record<
  string,
  {
    language: "sql" | "javascript" | "json";
    format: (value: string) => string;
  }
> = {
  "language-sql": {
    language: "sql",
    format: (value: string) => {
      try {
        return formatSql(value, { keywordCase: "upper" });
      } catch {
        return value;
      }
    },
  },
  "language-javascript": {
    language: "javascript",
    format: (value: string) => value,
  },
  "language-json": {
    language: "json",
    format: (value: string) => {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    },
  },
};
